// ═══════════════════════════════════════════════════════════
// 📦 SHARED ADMIN UTILITIES
// Single source of truth for design, types, DB helpers
// ═══════════════════════════════════════════════════════════

import { Platform } from 'react-native';
import { supabase } from '@/utils/supabase';

// ─── DESIGN SYSTEM ───────────────────────────────────────

export const COLORS = {
  bg: '#0D1117',
  bgSecondary: '#161B22',
  surface: '#1C2128',
  surfaceHover: '#222831',

  text: {
    primary: '#F0F6FC',
    secondary: '#8B949E',
    tertiary: '#484F58',
    disabled: '#30363D',
  },

  primary: '#7C3AED',
  primaryHover: '#6D28D9',
  primaryLight: 'rgba(124, 58, 237, 0.15)',
  primaryBorder: 'rgba(124, 58, 237, 0.4)',

  success: '#3FB950',
  successBg: 'rgba(63, 185, 80, 0.12)',
  successBorder: 'rgba(63, 185, 80, 0.3)',
  warning: '#D29922',
  warningBg: 'rgba(210, 153, 34, 0.12)',
  error: '#F85149',
  errorBg: 'rgba(248, 81, 73, 0.12)',
  errorBorder: 'rgba(248, 81, 73, 0.3)',
  info: '#58A6FF',
  infoBg: 'rgba(88, 166, 255, 0.12)',

  border: '#30363D',
  borderLight: '#21262D',

  chart: [
    '#7C3AED', '#58A6FF', '#3FB950',
    '#D29922', '#FF7CBB', '#39C5CF',
    '#F85149', '#BC8CFF',
  ],
};

export const SPACING = {
  xs: 4, sm: 8, md: 12, lg: 16,
  xl: 20, xxl: 24, xxxl: 32, huge: 48,
};

export const RADIUS = {
  xs: 4, sm: 6, md: 8, lg: 12,
  xl: 16, xxl: 20, full: 9999,
};

export const TYPOGRAPHY = {
  display: { fontSize: 44, fontWeight: '700' as const, lineHeight: 52, color: COLORS.text.primary },
  h1:      { fontSize: 28, fontWeight: '700' as const, lineHeight: 36, color: COLORS.text.primary },
  h2:      { fontSize: 22, fontWeight: '600' as const, lineHeight: 30, color: COLORS.text.primary },
  h3:      { fontSize: 17, fontWeight: '600' as const, lineHeight: 24, color: COLORS.text.primary },
  h4:      { fontSize: 15, fontWeight: '600' as const, lineHeight: 22, color: COLORS.text.primary },
  body:    { fontSize: 14, fontWeight: '400' as const, lineHeight: 20, color: COLORS.text.secondary },
  bodyMed: { fontSize: 14, fontWeight: '500' as const, lineHeight: 20, color: COLORS.text.primary },
  caption: { fontSize: 12, fontWeight: '400' as const, lineHeight: 18, color: COLORS.text.secondary },
  small:   { fontSize: 11, fontWeight: '500' as const, lineHeight: 16, color: COLORS.text.tertiary },
  mono:    {
    fontSize: 12,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier',
    color: COLORS.text.secondary,
  },
};

export const SHADOW = Platform.select({
  ios:     { shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.4, shadowRadius: 12 },
  android: { elevation: 6 },
  web:     { boxShadow: '0 4px 20px rgba(0,0,0,0.5)' },
}) as any;

// ─── TYPES ───────────────────────────────────────────────

export type RangeKey = 'today' | '7d' | '30d' | 'month' | 'custom';
export type TabKey   = 'sessions' | 'selections' | 'courses';

export interface TopicCount { topic: string; count: number; }

export interface DashboardStats {
  sessionsAllTime: number;
  sessionsRange: number;
  sessionsPrev: number;
  todaySessions: number;
  weekSessions: number;
  monthSessions: number;
  selectionsRange: number;
  selectionsPrev: number;
  topTopics: TopicCount[];
  topicsSampleTotal: number;
  uniqueTopicsSample: number;
  coursesTotal: number;
  coursesWithVouchers: number;
  coursesWithoutVouchers: number;
  updatedAt: string | null;
}

export interface DateRange {
  startISO: string; endISO: string;
  prevStartISO: string; prevEndISO: string;
  rangeLabel: string; rangeError: string | null;
}

// ─── DATE UTILS ──────────────────────────────────────────

export const startOfDay   = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
export const addDays      = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);
export const toIso        = (d: Date) => d.toISOString();
export const clamp0       = (n: number) => (Number.isFinite(n) ? Math.max(0, n) : 0);
export const calcTrendPct = (cur: number, prev: number) =>
  prev > 0 ? Math.round(((cur - prev) / prev) * 100) : 0;

export function parseYMD(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const [, y, mo, da] = m.map(Number);
  const d = new Date(y, mo - 1, da);
  return d.getFullYear() === y && d.getMonth() === mo - 1 && d.getDate() === da ? d : null;
}

export function formatTs(ts?: string | null): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('he-IL', {
      hour12: false, year: 'numeric', month: '2-digit',
      day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ts; }
}

export function formatRangeLabel(startISO: string, endISO: string): string {
  try {
    const o = { day: '2-digit' as const, month: '2-digit' as const };
    return `${new Date(startISO).toLocaleDateString('he-IL', o)} – ${new Date(endISO).toLocaleDateString('he-IL', o)}`;
  } catch { return '—'; }
}

export function calcDateRange(key: RangeKey, customStart: string, customEnd: string): DateRange {
  const now = new Date();

  const make = (start: Date, end: Date, label: string, err: string | null = null): DateRange => {
    const dur = end.getTime() - start.getTime();
    return {
      startISO: toIso(start), endISO: toIso(end),
      prevStartISO: toIso(new Date(start.getTime() - dur)),
      prevEndISO: toIso(start),
      rangeLabel: label, rangeError: err,
    };
  };

  if (key === 'today')  return make(startOfDay(now), now, 'היום');
  if (key === '7d')     return make(new Date(now.getTime() - 7  * 86400000), now, '7 ימים');
  if (key === '30d')    return make(new Date(now.getTime() - 30 * 86400000), now, '30 ימים');
  if (key === 'month')  return make(startOfMonth(now), now, 'חודש נוכחי');

  const sD = parseYMD(customStart);
  const eD = parseYMD(customEnd);
  const fallback = make(new Date(now.getTime() - 7 * 86400000), now, 'טווח מותאם', 'טווח לא תקין');
  if (!sD || !eD) return fallback;

  const start = startOfDay(sD);
  const end   = startOfDay(addDays(eD, 1));
  if (end.getTime() <= start.getTime()) return { ...fallback, rangeError: 'תאריך הסיום חייב להיות אחרי ההתחלה' };

  return make(start, end, formatRangeLabel(toIso(start), toIso(end)));
}

// ─── DB HELPERS ──────────────────────────────────────────

export async function safeCount(table: string): Promise<number> {
  try {
    const { count, error } = await supabase.from(table).select('*', { count: 'exact', head: true });
    if (error) { console.error(`[safeCount:${table}]`, error.message); return 0; }
    return count ?? 0;
  } catch (e) { console.error(`[safeCount:${table}] exception`, e); return 0; }
}

export async function safeCountInRange(
  table: string, tsCol: string, startISO: string, endISO: string
): Promise<number> {
  try {
    const { count, error } = await supabase.from(table)
      .select('*', { count: 'exact', head: true })
      .gte(tsCol, startISO).lt(tsCol, endISO);
    if (error) { console.error(`[safeCountInRange:${table}]`, error.message); return 0; }
    return count ?? 0;
  } catch (e) { console.error(`[safeCountInRange:${table}] exception`, e); return 0; }
}

export async function fetchTopTopics(
  startISO: string, endISO: string, limit = 5
): Promise<{ topics: TopicCount[]; total: number }> {
  try {
    // Primary: DB-side GROUP BY via RPC
    const { data, error } = await supabase.rpc('get_top_topics', {
      start_ts: startISO, end_ts: endISO, lim: limit,
    });
    if (!error && data) {
      const topics: TopicCount[] = (data as any[]).map((r) => ({
        topic: r.topic, count: Number(r.count),
      }));
      return { topics, total: topics.reduce((s, t) => s + t.count, 0) };
    }

    // Fallback: client aggregation (max 5000 rows)
    console.warn('[fetchTopTopics] RPC unavailable, using client fallback');
    const { data: rows } = await supabase.from('course_selections')
      .select('topic').not('topic', 'is', null)
      .gte('created_at', startISO).lt('created_at', endISO).limit(5000);
    if (!rows) return { topics: [], total: 0 };

    const counts: Record<string, number> = {};
    for (const r of rows) {
      const t = typeof r.topic === 'string' ? r.topic.trim() : '';
      if (t) counts[t] = (counts[t] || 0) + 1;
    }
    const topics = Object.entries(counts)
      .sort(([, a], [, b]) => b - a).slice(0, limit)
      .map(([topic, count]) => ({ topic, count }));
    return { topics, total: rows.length };
  } catch (e) {
    console.error('[fetchTopTopics] exception:', e);
    return { topics: [], total: 0 };
  }
}

export async function isAdmin(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.from('admins')
      .select('user_id').eq('user_id', userId).eq('is_active', true).maybeSingle();
    if (error) { console.error('[isAdmin]', error.message); return false; }
    return !!data;
  } catch (e) { console.error('[isAdmin] exception', e); return false; }
}